import store from '../../..';
import DocNameCellRenderer, {
  TDocNameCellRendererProps,
} from '../../../../components/common/generic/responsiveTable/renderers/DocNameCellRenderer';
import HTMLCellRenderer from '../../../../components/common/generic/responsiveTable/renderers/HTMLCellRenderer';
import HTMLAccordionRenderer from '../../../../components/common/generic/responsiveTable/renderers/HTMLAccordionRenderer';
import PriorityAccordionRenderer from '../../../../components/common/generic/responsiveTable/renderers/PriorityAccordionRenderer';
import PriorityRenderer from '../../../../components/common/generic/responsiveTable/renderers/PriorityRenderer';
import {
  TResponsiveTableRow,
  TResponsiveTableColumn,
  TResponsiveTableCell,
  TResponsiveTableCellRenderer,
} from '../../../../components/common/generic/responsiveTable/types';
import { TApiaRowDefinition, TApiaCellDefinition } from '../../../../types';
import { arrayOrArray, toBoolean } from '../../../../utils/util';
import { isComplexCell } from '../..';
import { TAccordionCellRenderer } from '../../../../components/common/generic/responsiveTable/renderers/AccordionCell';
import AccordionDocNameCellRenderer from '../../../../components/common/generic/responsiveTable/renderers/AccordionDocNameCellRenderer';

export type TRefreshDataRowParser<
  T extends { [key: string]: unknown } = { [key: string]: unknown },
> = (fieldId: string, rows: TApiaRowDefinition<T>[]) => TResponsiveTableRow[];

export function isPriorityCell(cell: TApiaCellDefinition) {
  return isComplexCell(cell) && cell.label?.includes('img/priority');
}
export function isStatusColumn(column: TResponsiveTableColumn) {
  return column.name.match(
    /^(pro_ele_inst_overdue_date|pro_ele_inst_warn_date|pro_inst_warn_date|pro_inst_overdue_date)$/,
  );
}

const defaultRowParser: TRefreshDataRowParser = (_, rows) => {
  return rows.map((row) => {
    const cells = arrayOrArray(row.cell);

    const returnRow: TResponsiveTableRow = {
      cells: cells.map((cell) => {
        if (typeof cell === 'string') {
          return { children: cell };
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let Renderer: TResponsiveTableCellRenderer<any> | undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let AccordionRenderer: TAccordionCellRenderer<any> | undefined;
        let rendererProps: Record<string, unknown> = {};

        if (isPriorityCell(cell)) {
          const returnCell: TResponsiveTableCell = {
            AccordionRenderer: PriorityAccordionRenderer,
            Renderer: PriorityRenderer,
            rendererProps: {
              src: cell.label,
            },
          };
          return returnCell;
        }
        if (cell.docName !== undefined) {
          AccordionRenderer = AccordionDocNameCellRenderer;
          Renderer = DocNameCellRenderer;
          const newProps: TDocNameCellRendererProps = {
            docName: cell.forceTitle ?? '',
            docUrl: cell.label,
          };
          rendererProps = newProps;
        } else if (cell.isHTML) {
          Renderer = HTMLCellRenderer;
          AccordionRenderer = HTMLAccordionRenderer;
          rendererProps = { html: cell.label };
        }
        const returnCell: TResponsiveTableCell = {
          AccordionRenderer,
          children: cell.label,
          className: cell.classToAdd,
          Renderer,
          rendererProps,
        };
        return returnCell;
      }),
      id: row.id,
      className: `${row.classToAdd ?? ''} ${
        row.unselectableTR ? 'locked' : ''
      } ${row.boldType ? 'bold' : ''} ${row['expired-doc'] ? 'expired' : ''}`,
      forbidSelection: row.unselectableTR,
      initiallySelected: toBoolean(row.selected),
    };
    return returnRow;
  });
};

export default defaultRowParser;

export function parseRows(
  listId: string,
  rows: TApiaRowDefinition[],
): TResponsiveTableRow[] {
  const state = store.getState().listSlice.lists[listId];
  const ev2: CustomEvent = new CustomEvent('clearError', { bubbles: true });
  const wrapperWithError = document.querySelectorAll('.dateInput [role=alert]');
  wrapperWithError.forEach((el) => {
    const input = el.closest('.dateInput')?.querySelector('input');
    if (input) {
      input.dispatchEvent(ev2);
    }
  });

  const newRows = (state?.rowsParser ?? defaultRowParser)(listId, rows);
  if (state?.hasAdditionalInfoInTable) {
    newRows.map((row) => row.cells.unshift({ children: '' }));
  }
  return newRows;
}
